/**
 * GraduationWizard — minimal "Keep these books" promote flow (04 §4.6).
 * Two hard-block confirmations (starting balances confirmed, fiscal year set)
 * each with a "set it up" link, then a Promote button calling promoteTrialOrg.
 * The checkboxes are UX affordances; the real gate is the backend, which
 * refuses with a failed-precondition naming any still-missing block — we
 * surface that verbatim rather than trusting the client checks.
 */
import { useCallback, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  Link,
  Typography,
  CircularProgress,
} from "@mui/material";
import CelebrationRoundedIcon from "@mui/icons-material/CelebrationRounded";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../services/use-auth";
import { tracedCallable } from "../../utilities/tracedCallable";

// Human labels for the backend's hard-block keys (whatever it names in the
// failed-precondition details/message, we map the known ones).
const BLOCK_LABELS = {
  starting_balances: "Starting balances confirmed",
  startingBalances: "Starting balances confirmed",
  fiscal_year: "Fiscal year set",
  fiscalYear: "Fiscal year set",
};

export default function GraduationWizard({ open, onClose, orgId }) {
  const { functions } = useAuth();
  const navigate = useNavigate();
  const [balancesOk, setBalancesOk] = useState(false);
  const [fiscalYearOk, setFiscalYearOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [missing, setMissing] = useState([]);
  const [done, setDone] = useState(false);

  const goto = useCallback(
    (path) => {
      onClose?.();
      navigate(`${path}?org=${orgId}`);
    },
    [navigate, orgId, onClose],
  );

  const promote = useCallback(async () => {
    setBusy(true);
    setError(null);
    setMissing([]);
    try {
      const call = tracedCallable(functions, "promoteTrialOrg");
      const res = await call({ orgId });
      if ((res?.data ?? res)?.promoted) setDone(true);
    } catch (e) {
      if (e?.code === "functions/failed-precondition") {
        // Backend names the still-missing hard blocks (details.missing[] or the
        // message). Show which, so the user knows what to finish.
        const detailsMissing = e?.details?.missing;
        if (Array.isArray(detailsMissing) && detailsMissing.length) {
          setMissing(detailsMissing);
        } else {
          setMissing([e.message].filter(Boolean));
        }
        setError("A couple of things need finishing before we can keep these books.");
      } else {
        Sentry.withScope((scope) => {
          scope.setTag("component", "GraduationWizard");
          scope.setTag("action", "promote");
          scope.setExtra("orgId", orgId);
          Sentry.captureException(e);
        });
        setError("Couldn't keep these books just now — please try again.");
      }
    } finally {
      setBusy(false);
    }
  }, [functions, orgId]);

  const labelFor = (key) => BLOCK_LABELS[key] || key;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      data-testid="graduation-wizard"
      aria-label="Keep these books">
      {done ? (
        <>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CelebrationRoundedIcon color="primary" /> These books are yours
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" data-testid="graduation-success">
              Your trial is now a real organization — nothing moved, nothing changed. Your cleanup
              list is waiting in your notifications.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={onClose} data-testid="graduation-done">
              Done
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>Keep these books</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Two things to confirm before these become your real books. You can change anything
              later.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={balancesOk}
                      onChange={(e) => setBalancesOk(e.target.checked)}
                      data-testid="graduation-check-balances"
                    />
                  }
                  label="Starting balances are confirmed"
                />
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  sx={{ ml: 4, display: "block" }}
                  onClick={() => goto("/foundation")}
                  data-testid="graduation-link-balances">
                  Review starting balances
                </Link>
              </Box>

              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={fiscalYearOk}
                      onChange={(e) => setFiscalYearOk(e.target.checked)}
                      data-testid="graduation-check-fiscal-year"
                    />
                  }
                  label="Fiscal year is set"
                />
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  sx={{ ml: 4, display: "block" }}
                  onClick={() => goto("/account-settings")}
                  data-testid="graduation-link-fiscal-year">
                  Open settings
                </Link>
              </Box>
            </Box>

            {error && (
              <Box sx={{ mt: 2 }} data-testid="graduation-error">
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
                {missing.length > 0 && (
                  <ul style={{ margin: "6px 0 0", paddingLeft: 18 }} data-testid="graduation-missing">
                    {missing.map((m) => (
                      <li key={m}>
                        <Typography variant="body2" color="error" component="span">
                          {labelFor(m)}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={busy} data-testid="graduation-cancel">
              Not yet
            </Button>
            <Button
              variant="contained"
              onClick={promote}
              disabled={busy || !balancesOk || !fiscalYearOk}
              data-testid="graduation-promote">
              {busy ? <CircularProgress size={18} /> : "Keep these books"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
